import React from "react";
import { motion } from "framer-motion";
import { Clock, Users, TrendingUp } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { commonLabels } from "@/i18n";

const AuctionExplainerSection = () => {
  const { language } = useLanguage();
  const labels = commonLabels(language);
  const sectionData =
    labels.menuPages?.howItWorks?.auctionExplainerSection || {};
  const features = sectionData.features || [];
  const stats = sectionData.stats || [];

  const icons = [Clock, Users, TrendingUp];
  const colors = [
    "from-blue-500 to-purple-500",
    "from-green-500 to-cyan-500",
    "from-purple-500 to-pink-500",
  ];

  return (
    <section className="py-12 md:py-20 bg-gradient-to-b from-blue-50 to-gray-900">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
            {sectionData.title || "Hur Våra Auktioner Fungerar"}
          </h2>
          <p className="text-base md:text-xl text-black max-w-3xl mx-auto leading-relaxed px-2">
            {sectionData.description ||
              "Modern auktionsplattform som skapar rättvis konkurrens och maximerar värdet för din bil."}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 items-center mb-12 md:mb-16">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <img
              src="/assets/bidding_example.png"
              alt="Auktionssida med budgivning och fordonsdetaljer"
              className="w-full h-auto rounded-xl md:rounded-2xl shadow-2xl"
              loading="lazy"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-6 md:space-y-8"
          >
            {features.map((feature: any, index: number) => {
              const Icon = icons[index] || icons[0];
              const color = colors[index] || colors[0];
              return (
                <div key={index} className="flex items-start">
                  <div
                    className={`flex-shrink-0 w-12 h-12 md:w-16 md:h-16 bg-gradient-to-r ${color} rounded-xl md:rounded-2xl flex items-center justify-center mr-4 md:mr-6`}
                  >
                    <Icon className="w-6 h-6 md:w-8 md:h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg md:text-2xl font-bold text-black mb-2 md:mb-3">
                      {feature.title || ""}
                    </h3>
                    <p className="text-sm md:text-base text-black leading-relaxed">
                      {feature.description || ""}
                    </p>
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>

        <div className="relative">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-gray-900 via-black to-gray-900 rounded-2xl md:rounded-3xl p-8 md:p-12 text-white"
          >
            <div className="text-center mb-8 md:mb-12">
              <h3 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6">
                {sectionData.comparisonTitle ||
                  "Därför Fungerar Vårt System Bättre"}
              </h3>
              <p className="text-gray-300 max-w-2xl mx-auto text-sm md:text-base">
                {sectionData.comparisonDescription ||
                  "Jämför med traditionella metoder och se varför allt fler väljer auktionsmodellen."}
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
              {stats.map((stat: any, index: number) => {
                const statColors = [
                  "text-emerald-400",
                  "text-blue-400",
                  "text-purple-400",
                  "text-orange-400",
                ];
                const color = statColors[index] || statColors[0];
                return (
                  <div
                    key={index}
                    className="text-center space-y-2 md:space-y-4"
                  >
                    <div className={`text-2xl md:text-4xl font-bold ${color}`}>
                      {stat.value || ""}
                    </div>
                    <div className="text-gray-300 font-semibold text-xs md:text-base">
                      {stat.label || ""}
                    </div>
                    <div className="text-xs md:text-sm text-gray-400">
                      {stat.description || ""}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AuctionExplainerSection;
